// Pure derivation of the Automatic-restart card's live summary sentence and retry timeline from the three field values.
// Kept React-free so the wording and the delay=0 / max=1 / max=0 branches are unit-testable in isolation; the observer
// component (`AutoRestartCard`) only renders what these return.

import { parseInt32 } from 'helpers/int32';

/** A run of summary text; `strong` marks the values the sentence emphasises (counts, codes, the "not"). */
export interface AutoRestartSegment {
  readonly text: string;
  readonly strong?: boolean;
}

/** The retry cycle rendered once with a repeat badge — not one node per attempt. `null` when nothing will be retried. */
export interface AutoRestartTimeline {
  readonly failLabel: string;
  // The "wait Ns" node is dropped when the delay is 0.
  readonly showWait: boolean;
  readonly waitLabel: string;
  readonly retryLabel: string;
  // "up to N×" repeat badge; null for a single retry (max = 1).
  readonly badge: string | null;
  readonly finalLabel: string;
}

export interface AutoRestartView {
  readonly summary: readonly AutoRestartSegment[];
  readonly timeline: AutoRestartTimeline | null;
}

export interface AutoRestartInput {
  readonly amount: number;
  readonly delaySeconds: number;
  readonly codes: readonly number[];
}

// "1" / "1 or 2" / "1, 2 or 3" — an Oxford-less natural list for the summary sentence.
const listCodes = (codes: readonly number[]): string => {
  if (codes.length === 1) return String(codes[0]);
  if (codes.length === 2) return `${codes[0]} or ${codes[1]}`;
  return `${codes.slice(0, -1).join(', ')} or ${codes[codes.length - 1]}`;
};

// The field values are raw model numbers (a free-text number input can hold e.g. a blanked or fractional value mid-edit);
// clamp to a non-negative integer for display, matching toSave()'s persistence clamp.
const clampCount = (value: number): number => {
  if (!Number.isFinite(value) || value < 0) return 0;
  return Math.floor(value);
};

/**
 * Build the card's summary segments + timeline for an ENABLED auto-restart. Callers gate on `enabled` themselves (a disabled
 * card shows neither). Mirrors the interactive mockup's wording exactly.
 */
export const buildAutoRestartView = ({ amount, delaySeconds, codes }: AutoRestartInput): AutoRestartView => {
  const n = clampCount(amount);
  const d = clampCount(delaySeconds);
  const hasCodes = codes.length > 0;

  // Enabled but capped at zero: nothing is ever retried. No timeline; a guard sentence instead.
  if (n === 0) {
    return {
      summary: [{ text: 'Enabled, but ' }, { text: 'Max auto-restarts is 0', strong: true }, { text: ' — nothing will be retried. Set it to 1 or more.' }],
      timeline: null,
    };
  }

  const summary: AutoRestartSegment[] = [{ text: 'If this node instance ' }];
  if (hasCodes) {
    summary.push({ text: 'fails with return code ' }, { text: listCodes(codes), strong: true });
  } else {
    summary.push({ text: 'fails' });
  }
  summary.push(
    { text: ', Acorn restarts it up to ' },
    { text: `${n}×`, strong: true },
    { text: ', waiting ' },
    { text: `${d}s`, strong: true },
    { text: ' between attempts. After the last attempt it stays failed.' }
  );
  if (hasCodes) {
    summary.push({ text: ' Failures with any other return code are ' }, { text: 'not', strong: true }, { text: ' retried.' });
  } else {
    summary.push({ text: ' Manual cancels and successful runs are never restarted.' });
  }

  return {
    summary,
    timeline: {
      failLabel: hasCodes ? `Fails (${codes.join(', ')})` : 'Run fails',
      showWait: d > 0,
      waitLabel: `wait ${d}s`,
      retryLabel: 'Retry',
      badge: n > 1 ? `up to ${n}×` : null,
      finalLabel: 'Still fails → Failed',
    },
  };
};

// Parse a raw text fragment (possibly comma-separated) into valid return codes: each token must be an integer within the
// backend column's signed-32-bit range (`parseInt32` drops blanks, non-integers, and over/underflow — including the
// `parseInt`-to-`Infinity` case). Used by the chips input on commit. Does not dedupe — the caller merges against the existing
// codes (dedupe there so it's against the authoritative list).
export const parseReturnCodeTokens = (raw: string): number[] =>
  raw
    .split(',')
    .map((part) => parseInt32(part))
    .filter((code): code is number => code !== null);
